import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Eye, EyeSlash } from "@phosphor-icons/react";

export default function Login() {
  const { login, error } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    await login(email, password);
    setBusy(false);
  };

  return (
    <div className="login-bg min-h-screen flex items-center justify-center px-6 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1718844054440-22acf5d5c8f0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhdGElMjBtZXNoJTIwZGFya3xlbnwwfHx8fDE3ODI1Njc0NTB8MA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          mixBlendMode: "screen",
        }}
      />
      <div className="absolute inset-0 bg-black/80" />
      <div className="relative w-full max-w-md">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <img
              src="/marqolix-logo.png"
              alt="Marqolix"
              data-testid="login-logo"
              className="h-10 w-10 object-contain"
            />
            <div className="w-2 h-2 bg-[var(--mq-green)]" />
            <span className="mq-heading text-2xl font-bold tracking-tighter">
              {t("appName")}
            </span>
          </div>
          <div className="flex gap-1 text-xs">
            <button
              data-testid="language-toggle-en"
              className={`mq-btn ${lang === "en" ? "mq-btn-primary" : ""}`}
              onClick={() => setLang("en")}
              style={{ padding: "0.2rem 0.5rem" }}
            >
              EN
            </button>
            <button
              data-testid="language-toggle-fr"
              className={`mq-btn ${lang === "fr" ? "mq-btn-primary" : ""}`}
              onClick={() => setLang("fr")}
              style={{ padding: "0.2rem 0.5rem" }}
            >
              FR
            </button>
          </div>
        </div>

        <div className="mq-panel p-8">
          <div className="mb-6">
            <div className="mq-label mb-1">{t("version")}</div>
            <h1 className="mq-heading text-3xl font-bold tracking-tighter mb-2">
              {t("loginTitle")}
            </h1>
            <p className="text-xs text-[var(--mq-text-2)]">{t("loginSubtitle")}</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="mq-label block mb-2">{t("email")}</label>
              <input
                data-testid="login-email-input"
                type="email"
                className="mq-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
              />
            </div>
            <div>
              <label className="mq-label block mb-2">{t("password")}</label>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  className="mq-input pr-10"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
                <button
                  data-testid="login-password-toggle"
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--mq-text-2)] hover:text-[var(--mq-green)] transition-colors"
                >
                  {showPassword ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
                </button>
              </div>
            </div>
            {error && (
              <div
                data-testid="login-error"
                className="text-xs text-[var(--mq-red)] py-2 border-l-2 border-[var(--mq-red)] pl-3"
              >
                {error}
              </div>
            )}
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="mq-btn mq-btn-primary w-full"
            >
              {busy ? t("signingIn") : t("signIn")}
            </button>
          </form>
        </div>

        <div className="mt-4 text-[10px] text-[var(--mq-text-3)] text-center tracking-widest uppercase">
          {t("tagline")}
        </div>
      </div>
    </div>
  );
}
